import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import { useAuthStore } from '../store/authStore';
import { analytics } from '../utils/analytics';
import { logger } from './logger';
import { errorService } from './error';

// Cache implementation
interface CacheItem {
  data: any;
  expiry: number;
}

class ApiCache {
  private cache: Map<string, CacheItem> = new Map();
  private readonly DEFAULT_TTL = 5 * 60 * 1000; // 5 minutes
  private readonly MAX_CACHE_SIZE = 100;

  constructor() {
    // Periodically clean expired cache items
    setInterval(() => this.cleanExpiredItems(), 60 * 1000); // Every minute
  }

  private cleanExpiredItems() {
    const now = Date.now();
    for (const [key, item] of this.cache.entries()) {
      if (item.expiry < now) {
        this.cache.delete(key);
      }
    }
  }

  private getCacheKey(url: string, params?: Record<string, any>): string {
    return `${url}${params ? `?${JSON.stringify(params)}` : ''}`;
  }

  get(url: string, params?: Record<string, any>): any | null {
    const key = this.getCacheKey(url, params);
    const item = this.cache.get(key);
    
    if (!item) return null;
    
    // Check if expired
    if (item.expiry < Date.now()) {
      this.cache.delete(key);
      return null;
    }
    
    return item.data;
  }

  set(url: string, data: any, params?: Record<string, any>, ttl: number = this.DEFAULT_TTL): void {
    // Ensure cache doesn't grow too large
    if (this.cache.size >= this.MAX_CACHE_SIZE) {
      // Remove oldest item
      const oldestKey = this.cache.keys().next().value;
      this.cache.delete(oldestKey);
    }
    
    const key = this.getCacheKey(url, params);
    this.cache.set(key, {
      data,
      expiry: Date.now() + ttl,
    });
  }

  invalidate(url: string, params?: Record<string, any>): void {
    const key = this.getCacheKey(url, params);
    this.cache.delete(key);
  }

  invalidateByPrefix(prefix: string): void {
    for (const key of this.cache.keys()) {
      if (key.startsWith(prefix)) {
        this.cache.delete(key);
      }
    }
  }

  clear(): void {
    this.cache.clear();
  }
}

const cache = new ApiCache();

// Create axios instance with default config
const api = axios.create({
  baseURL: '/api',
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 seconds
});

// Request interceptor
api.interceptors.request.use(
  (config) => {
    const startTime = Date.now();
    config.metadata = { startTime };

    // Add auth token if available
    const token = useAuthStore.getState().token;
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Add request ID for tracing
    const requestId = `req-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
    config.headers['X-Request-ID'] = requestId;

    // Track API request
    logger.debug(`API Request: ${config.method?.toUpperCase()} ${config.url}`, {
      requestId,
      params: config.params,
      data: config.data,
    });

    return config;
  },
  (error) => {
    logger.error('API request error', { error });
    errorService.handleError(error, { context: 'api.request' });
    return Promise.reject(error);
  }
);

// Response interceptor
api.interceptors.response.use(
  (response) => {
    const endTime = Date.now();
    const startTime = response.config.metadata?.startTime;
    const duration = startTime ? endTime - startTime : 0;
    
    // Add response time
    response.duration = duration;
    
    // Log successful response
    logger.debug(`API Response: ${response.config.method?.toUpperCase()} ${response.config.url}`, {
      status: response.status,
      duration,
      requestId: response.config.headers['X-Request-ID'],
    });

    // Track performance metrics for slow responses
    if (duration > 1000) { // Responses taking more than 1 second
      analytics.trackEvent({
        category: 'Performance',
        action: 'slow_api_response',
        label: `${response.config.method} ${response.config.url}`,
        value: duration,
      });
    }

    return response;
  },
  async (error: AxiosError) => {
    const endTime = Date.now();
    const config = error.config as AxiosRequestConfig & { 
      metadata?: { startTime: number };
      _retry?: boolean;
    };
    const startTime = config?.metadata?.startTime;
    const duration = startTime ? endTime - startTime : 0;
    
    // Log error response
    logger.warn(`API Error: ${config?.method?.toUpperCase()} ${config?.url}`, {
      status: error.response?.status,
      duration,
      message: error.message,
      requestId: config?.headers?.['X-Request-ID'],
    });

    // Handle token expiration
    if (error.response?.status === 401 && !config?._retry) {
      config._retry = true;
      
      try {
        // Attempt to refresh token
        const authStore = useAuthStore.getState();
        if (authStore.token) {
          // In a real app, this would call a token refresh endpoint
          // For now, just log out the user
          authStore.logout();
          window.location.href = '/login?session=expired';
        }
      } catch (refreshError) {
        return Promise.reject(refreshError);
      }
    }

    // Retry logic for network errors and 5xx responses
    if (
      (error.code === 'ECONNABORTED' || 
       error.message.includes('Network Error') || 
       (error.response && error.response.status >= 500)) && 
      !config?._retry
    ) {
      config._retry = true;
      
      // Exponential backoff
      const delay = Math.min(1000 * (Math.random() + 1), 5000);
      
      logger.info(`Retrying request after ${delay}ms: ${config?.method} ${config?.url}`);
      
      await new Promise(resolve => setTimeout(resolve, delay));
      return api(config);
    }

    // Track error in analytics
    analytics.trackEvent({
      category: 'API',
      action: 'error',
      label: `${config?.method} ${config?.url}`,
      metadata: {
        status: error.response?.status,
        message: error.message,
        duration,
      },
    });

    // Handle error with error service
    errorService.handleError(error, {
      context: 'api.response',
      url: config?.url,
      method: config?.method,
      status: error.response?.status,
    });

    return Promise.reject(error);
  }
);

// API methods with caching support
const apiClient = {
  get: async <T>(url: string, config: AxiosRequestConfig = {}) => {
    const { useCache = false, cacheTTL, ...axiosConfig } = config as AxiosRequestConfig & {
      useCache?: boolean;
      cacheTTL?: number;
    };
    
    // Try to get from cache if caching is enabled
    if (useCache) {
      const cachedData = cache.get(url, axiosConfig.params);
      if (cachedData) {
        logger.debug(`Cache hit for ${url}`, { params: axiosConfig.params });
        return cachedData;
      }
    }
    
    // Make the request
    const response = await api.get<T>(url, axiosConfig);
    
    // Cache the response if caching is enabled
    if (useCache) {
      cache.set(url, response.data, axiosConfig.params, cacheTTL);
    }
    
    return response.data;
  },

  post: async <T>(url: string, data = {}, config: AxiosRequestConfig = {}) => {
    const { invalidateCache, invalidateCachePrefix, ...axiosConfig } = config as AxiosRequestConfig & {
      invalidateCache?: string[];
      invalidateCachePrefix?: string[];
    };
    
    const response = await api.post<T>(url, data, axiosConfig);
    
    // Invalidate cache if specified
    if (invalidateCache) {
      invalidateCache.forEach(cacheUrl => {
        cache.invalidate(cacheUrl);
      });
    }
    
    if (invalidateCachePrefix) {
      invalidateCachePrefix.forEach(prefix => {
        cache.invalidateByPrefix(prefix);
      });
    }
    
    return response.data;
  },

  put: async <T>(url: string, data = {}, config: AxiosRequestConfig = {}) => {
    const { invalidateCache, invalidateCachePrefix, ...axiosConfig } = config as AxiosRequestConfig & {
      invalidateCache?: string[];
      invalidateCachePrefix?: string[];
    };
    
    const response = await api.put<T>(url, data, axiosConfig);
    
    // Invalidate cache if specified
    if (invalidateCache) {
      invalidateCache.forEach(cacheUrl => {
        cache.invalidate(cacheUrl);
      });
    }
    
    if (invalidateCachePrefix) {
      invalidateCachePrefix.forEach(prefix => {
        cache.invalidateByPrefix(prefix);
      });
    }
    
    return response.data;
  },

  delete: async <T>(url: string, config: AxiosRequestConfig = {}) => {
    const { invalidateCache, invalidateCachePrefix, ...axiosConfig } = config as AxiosRequestConfig & {
      invalidateCache?: string[];
      invalidateCachePrefix?: string[];
    };
    
    const response = await api.delete<T>(url, axiosConfig);
    
    // Invalidate cache if specified
    if (invalidateCache) {
      invalidateCache.forEach(cacheUrl => {
        cache.invalidate(cacheUrl);
      });
    }
    
    if (invalidateCachePrefix) {
      invalidateCachePrefix.forEach(prefix => {
        cache.invalidateByPrefix(prefix);
      });
    }
    
    return response.data;
  },

  patch: async <T>(url: string, data = {}, config: AxiosRequestConfig = {}) => {
    const { invalidateCache, invalidateCachePrefix, ...axiosConfig } = config as AxiosRequestConfig & {
      invalidateCache?: string[];
      invalidateCachePrefix?: string[];
    };
    
    const response = await api.patch<T>(url, data, axiosConfig);
    
    // Invalidate cache if specified
    if (invalidateCache) {
      invalidateCache.forEach(cacheUrl => {
        cache.invalidate(cacheUrl);
      });
    }
    
    if (invalidateCachePrefix) {
      invalidateCachePrefix.forEach(prefix => {
        cache.invalidateByPrefix(prefix);
      });
    }
    
    return response.data;
  },

  // Cache control methods
  cache: {
    invalidate: (url: string, params?: Record<string, any>) => {
      cache.invalidate(url, params);
    },
    invalidateByPrefix: (prefix: string) => {
      cache.invalidateByPrefix(prefix);
    },
    clear: () => {
      cache.clear();
    },
  },
};

export default apiClient;

// ----------------- Habits API (Custom) -----------------

export const fetchHabits = () => apiClient.get('/habits', { useCache: true });

export const createHabit = (habit: {
  name: string;
  description?: string;
  frequency: 'daily' | 'weekly' | 'monthly' | 'multiple';
  selectedDay?: number;
  selectedDays?: number[];
  category?: string;
}) => apiClient.post('/habits', habit, {
  invalidateCache: ['/habits'],
});

export const updateHabit = (id: string, updates: Partial<{
  name: string;
  description?: string;
  frequency: 'daily' | 'weekly' | 'monthly' | 'multiple';
  selectedDay?: number;
  selectedDays?: number[];
  category?: string;
}>) => apiClient.patch(`/habits/${id}`, updates, {
  invalidateCache: ['/habits'],
});

export const deleteHabit = (id: string) => apiClient.delete(`/habits/${id}`, {
  invalidateCache: ['/habits'],
});

export const setHabitStatus = (
  id: string,
  payload: { date: string; status: 'success' | 'fail' | 'skip'; comment?: string }
) => apiClient.patch(`/habits/${id}/status`, payload, {
  invalidateCache: ['/habits'],
});
